import { useCallback } from 'react'
import {
  type DefaultAvatarCacheEntry,
  deleteAvatarCache,
  deleteDefaultAvatarCache,
  getAvatarCache,
  getDefaultAvatarCache,
  setAvatarCache,
  setDefaultAvatarCache,
  touchDefaultAvatarCache,
} from '@/shared/lib/avatarCache'
import { readAvatarFailure, reportAvatarFailure } from '@/shared/lib/avatarDiagnostics'
import type { AvatarGetResponse } from '../api/getAvatar'
import { getAvatarVrm, getHubAvatarVrm } from '../api/getAvatarVrm'
import { DEFAULT_AVATAR_NAME, getDefaultAvatar } from '../api/getDefaultAvatar'
import { isValidVrmBlob } from '../lib/vrmBlobValidation'
import type { HubSelectionHandoff } from './hubSettingsTypes'

const DEFAULT_AVATAR_CACHE_REVALIDATE_INTERVAL_MS = 24 * 60 * 60 * 1000
type Props = {
  isCurrent: (syncId: number) => boolean
  onResolved: (syncId: number, blob: Blob) => boolean
}

/** 取得元ごとのキャッシュと取得方法を担当し、通知・Object URL・再試行契機を持たない。 */
export const useAvatarSources = ({ isCurrent, onResolved }: Props) => {
  const revalidateDefaultAvatar = useCallback(
    async (syncId: number, entry: DefaultAvatarCacheEntry) => {
      try {
        const result = await getDefaultAvatar({ avatarName: entry.avatarName, ifNoneMatch: entry.avatarVersion })
        if (!isCurrent(syncId)) return
        const validatedAt = Date.now()
        if (result.status === 'not_modified') {
          await touchDefaultAvatarCache(entry.avatarName, validatedAt)
          return
        }
        await setDefaultAvatarCache({
          avatarName: entry.avatarName,
          avatarVersion: result.avatarVersion,
          vrmBlob: result.vrmBlob,
          cachedAt: validatedAt,
          validatedAt,
        })
        onResolved(syncId, result.vrmBlob)
      } catch (error) {
        // 有効なcacheを表示済みなので、背景再検証の失敗で表示を破棄しない。
        const { status, code } = readAvatarFailure(error)
        reportAvatarFailure('blob_download', status, code)
      }
    },
    [isCurrent, onResolved]
  )
  const resolveDefaultAvatar = useCallback(
    async (syncId: number): Promise<boolean> => {
      const entry = await getDefaultAvatarCache(DEFAULT_AVATAR_NAME)
      if (!isCurrent(syncId)) return false
      if (entry) {
        if (!(await isValidVrmBlob(entry.vrmBlob))) await deleteDefaultAvatarCache(entry.avatarName)
        else {
          onResolved(syncId, entry.vrmBlob)
          if (Date.now() - entry.validatedAt >= DEFAULT_AVATAR_CACHE_REVALIDATE_INTERVAL_MS)
            void revalidateDefaultAvatar(syncId, entry)
          return true
        }
      }
      try {
        const result = await getDefaultAvatar()
        if (!isCurrent(syncId) || result.status !== 'ok') return false
        const savedAt = Date.now()
        await setDefaultAvatarCache({
          avatarName: result.avatarName,
          avatarVersion: result.avatarVersion,
          vrmBlob: result.vrmBlob,
          cachedAt: savedAt,
          validatedAt: savedAt,
        })
        return onResolved(syncId, result.vrmBlob)
      } catch (error) {
        const { status, code } = readAvatarFailure(error)
        reportAvatarFailure('blob_download', status, code)
        return false
      }
    },
    [isCurrent, onResolved, revalidateDefaultAvatar]
  )
  const resolveUploadAvatar = useCallback(
    async (syncId: number, userId: string, avatarVersion: string): Promise<boolean> => {
      const entry = await getAvatarCache(userId)
      if (!isCurrent(syncId)) return false
      if (entry?.avatarVersion === avatarVersion) {
        if (await isValidVrmBlob(entry.vrmBlob)) return onResolved(syncId, entry.vrmBlob)
        await deleteAvatarCache(userId)
      }
      const blob = await getAvatarVrm(avatarVersion)
      if (!isCurrent(syncId)) return false
      await setAvatarCache({ userId, avatarVersion, vrmBlob: blob, cachedAt: Date.now() })
      return onResolved(syncId, blob)
    },
    [isCurrent, onResolved]
  )
  const resolveHubAvatar = useCallback(
    async (syncId: number, avatar: AvatarGetResponse, candidate?: HubSelectionHandoff): Promise<boolean> => {
      // Hub由来はIndexedDBを一切通さず、同じモデルID・版の確定済み候補だけを再利用する。
      const blob =
        candidate &&
        candidate.modelId === avatar.vroidHubCharacterModelId &&
        candidate.avatarVersion === avatar.avatarVersion
          ? candidate.hubBlob
          : await getHubAvatarVrm()
      return onResolved(syncId, blob)
    },
    [onResolved]
  )
  return { resolveDefaultAvatar, resolveUploadAvatar, resolveHubAvatar }
}
